import { CommodityField } from "components/fields/CommodityField";
import { CoolerTypeField } from "components/fields/CoolerTypeField";
import { MyDatagrid } from "components/HistoryGrid/Grid";
import { createCellStyle } from "components/HistoryGrid/utils";
import { useTerminalsContext } from "contexts/TerminalsProvider";
import { ExtraWorkCountFields } from "entities/ExtraWorkField";
import React from "react";
import { FunctionField, NumberField, ReferenceField, TextField, usePermissions } from "react-admin";
import { useSelector } from "react-redux";
import { useAsyncColumnsStorage } from "utils/hooks/useAsyncColumnsStorage";
import { isStorageModuleEnabled } from "utils/storageModule";

import { DateTimeField } from "../../../../components/fields/DateFormatField";
import { FakeReferenceTextField } from "../../../../components/fields/FakeReferenceTextField";
import { CountryISOCodeField } from "../../../../components/fields/CountryISOCodeField";
import { LongTextField } from "../../../../components/fields/LongTextField";
import { NoneOrValueField } from "../../../../components/fields/NoOrValueField";
import { PriorityField } from "../../../../components/fields/PriorityField";
import { SelectEnumField } from "../../../../components/fields/SelectEnumField";
import { SelectTextField } from "../../../../components/fields/SelectTextField";
import { TextPopoverField } from "../../../../components/fields/TextPopoverField";
import { YesNoField } from "../../../../components/fields/YesNoField";
import { OrderService, OrderServiceNames } from "../../../../entities";
import { CorrectionType } from "../../../../entities/CorrectionType";
import { HideableOrderField } from "../../../../entities/HideableOrderField";
import { LabelSource } from "../../../../entities/LabelSource";
import { useTerminalFieldVisibility } from "../../../../hooks/useTerminalFieldVisibility";

const PlaceNameField = ({ record, terminals }) => {
	const usesStorageModule = isStorageModuleEnabled(terminals[record?.terminal_id]);

	return (
		<FakeReferenceTextField
			record={record}
			source={usesStorageModule ? "storage_place_id" : "area_id"}
			replaceSource={usesStorageModule ? "storage_place_name" : "area_name"}
		/>
	);
};

export const OrderHistoryGrid = ({ nextForRecordId, orderRecord, ...props }) => {
	const statuses = useSelector((state) => state.admin.resources["order-statuses"].data);
	const columnsAsyncStorage = useAsyncColumnsStorage("order-history-columns");
	const { data: terminals } = useTerminalsContext();

	const rowStyle = React.useCallback(
		(record) => {
			return {
				backgroundColor: statuses[record.status] && statuses[record.status].color,
			};
		},
		[statuses]
	);

	const cellStyle = createCellStyle(props.data, nextForRecordId, ["updated_at"]);

	const { permissions } = usePermissions();
	const isCS = orderRecord?.service === OrderService.coldStorage;
	const { isFieldHidden } = useTerminalFieldVisibility(orderRecord?.terminal_id);
	const orderUsesStorageModule = isStorageModuleEnabled(terminals[orderRecord?.terminal_id]);
	const orderPlaceField = orderUsesStorageModule ? "storage_place_id" : "area_id";

	if (!permissions) return null;

	return (
		<MyDatagrid
			{...props}
			rowStyle={rowStyle}
			cellStyle={cellStyle}
			useCustomDataGridWithHideShowColumn={true}
			storage={columnsAsyncStorage}
		>
			<TextField
				sortable={false}
				source="updated_by_name"
				label="resources.orders.fields.updated_by"
			/>
			<DateTimeField
				sortable={false}
				source="updated_at"
				label="resources.orders.fields.updated_at"
			/>
			<TextField sortable={false} source="rank" label="resources.tasks.fields.rank" />
			<TextField sortable={false} source="rank_auto" label="resources.tasks.fields.rank_auto" />
			<TextField sortable={false} source="reference" label="resources.orders.fields.reference" />
			<SelectEnumField
				sortable={false}
				source="service"
				enumObj={OrderServiceNames}
				translate={false}
				reverse
				label="resources.orders.fields.service"
			/>
			<ReferenceField
				sortable={false}
				source="terminal_id"
				label="resources.orders.fields.terminal"
				reference="terminal"
				link={false}
			>
				<TextField source="name" />
			</ReferenceField>
			<PriorityField sortable={false} source="priority" label="resources.orders.fields.priority" />
			<ReferenceField
				sortable={false}
				source="status"
				label="resources.orders.fields.status"
				reference="order-statuses"
				link={false}
			>
				<TextField source="name" />
			</ReferenceField>
			<ReferenceField
				sortable={false}
				source="company_id"
				label="resources.orders.fields.company_id"
				reference="customer-companies"
				link={false}
			>
				<TextField source="full_name" />
			</ReferenceField>
			{!isFieldHidden(HideableOrderField.COOLER_TYPE_ID) && (
				<CoolerTypeField sortable={false} label="resources.orders.fields.cooler_type_id" />
			)}
			{!isFieldHidden(HideableOrderField.PLACE_FROM) && (
				<LongTextField source="place_from" label="resources.order-history.fields.place_from" />
			)}
			{!isFieldHidden(HideableOrderField.PLACE_FROM_COUNTRY_CODE) && (
				<CountryISOCodeField source="place_from_country_code" label="resources.order-history.fields.place_from_country_code" />
			)}
			{!isFieldHidden("eta_external_terminal_id") && (
				<ReferenceField
					source="eta_external_terminal_id"
					reference="external-terminal-links"
					label="resources.order-history.fields.eta_external_terminal_id"
					link={false}
				>
					<TextField source="external_name" />
				</ReferenceField>
			)}
			<TextField sortable={false} source="eta_date" label="resources.orders.fields.eta_date" />
			<TextField sortable={false} source="eta_time" label="resources.orders.fields.eta_time" />
			<TextField
				sortable={false}
				source="eta_slot_time"
				label="resources.orders.fields.eta_slot_time"
			/>
			<FakeReferenceTextField
				sortable={false}
				source="port_in"
				replaceSource="port_in_name"
				label="resources.orders.fields.port_in"
			/>
			{!isFieldHidden(HideableOrderField.ETA_3RD_PARTY) && (
				<ReferenceField
					label="resources.orders.fields.eta_3rd_party"
					sortable={false}
					source="eta_3rd_party"
					reference="customer-companies"
					link={false}
				>
					<TextField source="full_name" />
				</ReferenceField>
			)}
			{!isFieldHidden(HideableOrderField.ETA_4TH_PARTY) && (
				<ReferenceField
					label="resources.orders.fields.eta_4th_party"
					sortable={false}
					source="eta_4th_party"
					reference="customer-companies"
					link={false}
				>
					<TextField source="full_name" />
				</ReferenceField>
			)}
			{!isFieldHidden(HideableOrderField.ETA_TRUCK) && (
				<SelectTextField
					sortable={false}
					source="eta_truck"
					refSource="eta_truck_id"
					reference="trucks"
				/>
			)}
			{!isFieldHidden(HideableOrderField.ETA_TRAILER) && (
				<SelectTextField
					sortable={false}
					source="eta_trailer"
					refSource="eta_trailer_id"
					reference="trailers"
				/>
			)}
			{!isFieldHidden(HideableOrderField.ETA_DRIVER) && (
				<SelectTextField
					sortable={false}
					source="eta_driver"
					refSource="eta_driver_id"
					reference="users"
				/>
			)}
			{!isFieldHidden(HideableOrderField.ETA_DRIVER_PHONE) && (
				<TextField
					sortable={false}
					source="eta_driver_phone"
					label="resources.order-history.fields.eta_driver_phone"
				/>
			)}
			{!isFieldHidden(HideableOrderField.ETA_SHOW_DOC_FOR_TRANS) && (
				<YesNoField
					sortable={false}
					source="eta_show_doc_for_trans"
					label="resources.order-history.fields.eta_show_doc"
				/>
			)}
			<YesNoField
				sortable={false}
				source="is_eta_arrived"
				label="resources.orders.fields.is_eta_arrived"
			/>
			<YesNoField
				sortable={false}
				source="is_eta_3rd_party_reload"
				label="resources.orders.fields.is_eta_3rd_party_reload"
			/>
			{!isFieldHidden(HideableOrderField.BOXES) && (
				<NumberField sortable={false} source="boxes" label="resources.orders.fields.boxes" />
			)}
			{!isFieldHidden(HideableOrderField.LABEL) && (
				<NumberField sortable={false} source="label" label="resources.orders.fields.label" />
			)}
			{!isFieldHidden(HideableOrderField.LABEL_SOURCE) && (
				<SelectEnumField source="label_source" enumName="label_source" enumObj={LabelSource} />
			)}
			{!isFieldHidden(HideableOrderField.PALLETS) && (
				<NumberField sortable={false} source="pallets" label="resources.orders.fields.pallets" />
			)}
			{!isFieldHidden(HideableOrderField.KILOS) && (
				<NumberField sortable={false} source="kilos" label="resources.orders.fields.kilos" />
			)}
			{!isFieldHidden(HideableOrderField.IS_DOC_FOR_NFSA) && (
				<YesNoField
					sortable={false}
					source="is_doc_for_nfsa"
					label="resources.orders.fields.is_doc_for_nfsa"
				/>
			)}
			{!isFieldHidden(HideableOrderField.IS_PER) && (
				<YesNoField
					sortable={false}
					source="is_per"
					label="resources.orders.fields.is_per"
				/>
			)}
			{!isFieldHidden(HideableOrderField.COMMODITY_ID) && (
				<CommodityField
					sortable={false}
					source="commodity_id"
					label="resources.orders.fields.commodity_id"
				/>
			)}
			<YesNoField sortable={false} source="split_load" label="resources.orders.fields.split_load" />
			{!isFieldHidden("outgoing_trip_number") && (
				<TextField
					sortable={false}
					source="outgoing_trip_number"
					label="resources.orders.fields.outgoing_trip_number"
				/>
			)}
			{!isFieldHidden("loading_sequence") && (
				<NumberField
					sortable={false}
					source="loading_sequence"
					label="resources.orders.fields.loading_sequence"
				/>
			)}
			<NumberField sortable={false} source="load_no" label="resources.orders.fields.load_no" />
			{!isCS
				? [
						<TextField
							sortable={false}
							source="etd_date"
							label="resources.orders.fields.etd_date"
						/>,
						<TextField
							sortable={false}
							source="etd_time"
							label="resources.orders.fields.etd_time"
						/>,
						<TextField
							sortable={false}
							source="etd_slot_time"
							label="resources.orders.fields.etd_slot_time"
						/>,
						<FakeReferenceTextField
							sortable={false}
							source="port_out"
							replaceSource="port_out_name"
							label="resources.orders.fields.port_out"
						/>,
						<FakeReferenceTextField
							sortable={false}
							source="optimal_port_out"
							replaceSource="optimal_port_out_name"
							label="resources.orders.fields.optimal_port_out"
						/>,
						!isFieldHidden(HideableOrderField.ETD_3RD_PARTY) ? (
							<ReferenceField
								label="resources.orders.fields.etd_3rd_party"
								sortable={false}
								source="etd_3rd_party"
								reference="customer-companies"
								link={false}
							>
								<TextField source="full_name" />
							</ReferenceField>
						) : null,
						!isFieldHidden(HideableOrderField.ETD_4TH_PARTY) ? (
							<ReferenceField
								label="resources.orders.fields.etd_4th_party"
								sortable={false}
								source="etd_4th_party"
								reference="customer-companies"
								link={false}
							>
								<TextField source="full_name" />
							</ReferenceField>
						) : null,
						!isFieldHidden(HideableOrderField.PLACE_TO) ? (
							<LongTextField source="place_to" label="resources.order-history.fields.place_to" />
						) : null,
						!isFieldHidden(HideableOrderField.PLACE_TO_COUNTRY_CODE) ? (
							<CountryISOCodeField source="place_to_country_code" label="resources.order-history.fields.place_to_country_code" />
						) : null,
						!isFieldHidden("etd_external_terminal_id") ? (
							<ReferenceField
								source="etd_external_terminal_id"
								reference="external-terminal-links"
								label="resources.order-history.fields.etd_external_terminal_id"
								link={false}
							>
								<TextField source="external_name" />
							</ReferenceField>
						) : null,
						!isFieldHidden(HideableOrderField.ETD_TRUCK) ? (
							<SelectTextField
								sortable={false}
								source="etd_truck"
								refSource="etd_truck_id"
								reference="trucks"
							/>
						) : null,
						!isFieldHidden(HideableOrderField.ETD_TRAILER) ? (
							<SelectTextField
								sortable={false}
								source="etd_trailer"
								refSource="etd_trailer_id"
								reference="trailers"
							/>
						) : null,
						!isFieldHidden(HideableOrderField.ETD_DRIVER) ? (
							<SelectTextField
								sortable={false}
								source="etd_driver"
								refSource="etd_driver_id"
								reference="users"
							/>
						) : null,
						!isFieldHidden(HideableOrderField.ETD_DRIVER_PHONE) ? (
							<TextField
								sortable={false}
								source="etd_driver_phone"
								label="resources.order-history.fields.etd_driver_phone"
							/>
						) : null,
						!isFieldHidden(HideableOrderField.ETD_SHOW_DOC_FOR_TRANS) ? (
							<YesNoField
								sortable={false}
								source="etd_show_doc_for_trans"
								label="resources.order-history.fields.etd_show_doc"
							/>
						) : null,
						<YesNoField
							sortable={false}
							source="is_etd_arrived"
							label="resources.orders.fields.is_etd_arrived"
						/>,
						<YesNoField
							sortable={false}
							source="is_etd_3rd_party_reload"
							label="resources.orders.fields.is_etd_3rd_party_reload"
						/>,
				  ]
				: [
						<YesNoField
							sortable={false}
							source="is_reefer"
							label="resources.orders.fields.is_reefer"
						/>,
						<TextField
							sortable={false}
							source="container_number"
							label="resources.orders.fields.container_number"
						/>,
						<NumberField
							sortable={false}
							source="total_boxes"
							label="resources.orders.fields.total_boxes"
						/>,
						<YesNoField
							sortable={false}
							source="is_photo_of_label"
							label="resources.orders.fields.is_photo_of_label"
						/>,
				  ]}
			{!isFieldHidden(HideableOrderField.PLUMB) && (
				<YesNoField sortable={false} source="plumb" label="resources.orders.fields.plumb" />
			)}
			{!isFieldHidden(HideableOrderField.PLUMB_NUMBER) && (
				<TextField
					sortable={false}
					source="plumb_number"
					label="resources.orders.fields.plumb_number"
				/>
			)}
			{!isFieldHidden(HideableOrderField.SEAL) && (
				<YesNoField sortable={false} source="seal" label="resources.orders.fields.seal" />
			)}
			{!isFieldHidden(HideableOrderField.SEAL_NUMBER) && (
				<TextField
					sortable={false}
					source="seal_number"
					label="resources.orders.fields.seal_number"
				/>
			)}
			{!isFieldHidden(HideableOrderField.PRINTED_DOC) && (
				<NoneOrValueField
					sortable={false}
					source="printed_doc"
					label="resources.orders.fields.printed_doc"
				/>
			)}
			{!isFieldHidden(HideableOrderField.EXTRA_LOADING_TIME) && (
				<TextField
					sortable={false}
					source="extra_loading_time"
					label="resources.orders.fields.extra_loading_time"
				/>
			)}
			{!isFieldHidden("is_extra_loading_time_billable") && (
				<YesNoField
					sortable={false}
					source="is_extra_loading_time_billable"
					label="resources.orders.fields.is_extra_loading_time_billable"
				/>
			)}
			{ExtraWorkCountFields.filter((source) => !isFieldHidden(source)).map((source) => (
				<TextField
					key={source}
					sortable={false}
					source={source}
					label={`resources.orders.fields.${source}`}
				/>
			))}
			{!orderUsesStorageModule && !isFieldHidden(HideableOrderField.IS_AREA_BLOCKED) ? (
				<YesNoField
					sortable={false}
					source="is_area_blocked"
					label="resources.orders.fields.is_area_blocked"
				/>
			) : null}
			<FunctionField
				sortable={false}
				source={orderPlaceField}
				label={`resources.orders.fields.${orderPlaceField}`}
				render={(record) => <PlaceNameField record={record} terminals={terminals} />}
			/>
			{!isFieldHidden(HideableOrderField.ASSIGNEE_1) && (
				<ReferenceField
					sortable={false}
					source="assignee_1"
					label="resources.orders.fields.assignee_1"
					reference="company-users"
					filter={{ no_range: true }}
					link={false}
				>
					<TextField source="full_name" />
				</ReferenceField>
			)}
			{!isFieldHidden(HideableOrderField.ASSIGNEE_2) && (
				<ReferenceField
					sortable={false}
					source="assignee_2"
					label="resources.orders.fields.assignee_2"
					reference="company-users"
					filter={{ no_range: true }}
					link={false}
				>
					<TextField source="full_name" />
				</ReferenceField>
			)}
			{isCS && !isFieldHidden(HideableOrderField.ASSIGNEE_3) ? (
				<ReferenceField
					sortable={false}
					source="assignee_3"
					label="resources.orders.fields.assignee_3"
					reference="company-users"
					filter={{ no_range: true }}
					link={false}
				>
					<TextField source="full_name" />
				</ReferenceField>
			) : null}
			<YesNoField
				sortable={false}
				source="set_order_completed"
				label="resources.orders.fields.set_order_completed"
			/>
			<SelectEnumField
				sortable={false}
				source="correction_type"
				enumName="correction_type"
				enumObj={CorrectionType}
				label="resources.orders.fields.correction_type"
			/>
			<TextPopoverField source="notes" label="resources.orders.fields.notes" />
			<TextPopoverField source="internal_notes" label="resources.orders.fields.internal_notes" />
		</MyDatagrid>
	);
};
